/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

/**
 * OSCHEANO audit CTA routing:
 * - Normalize every project/audit CTA.
 * - Create a real same-page form anchor.
 * - Handle late-rendered Shopify sections and hash changes.
 */
(function () {
  const destination = '/pages/contact#audit-form';
  const auditLabels = /audit|project|review|discuss your goals|start the process|project form|email project details/i;

  function isAuditLink(link) {
    const label = (link.textContent || '').trim();
    const href = link.getAttribute('href') || '';

    return (
      link.classList.contains('osx-cta') ||
      (link.classList.contains('osx-btn') && auditLabels.test(label)) ||
      auditLabels.test(label) ||
      href === destination
    );
  }

  function getAuditTarget() {
    const form = document.querySelector('.contact-form');
    if (!form) return null;

    const target = form.closest('.shopify-section') || form;
    if (!document.getElementById('audit-form')) {
      target.id = 'audit-form';
    }
    target.style.scrollMarginTop = '96px';

    return target;
  }

  function scrollToAudit() {
    if (window.location.pathname !== '/pages/contact' || window.location.hash !== '#audit-form') {
      return false;
    }

    const target = getAuditTarget();
    if (!target) return false;

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    return true;
  }

  function configureOscheanoAuditCtas() {
    document.querySelectorAll('a').forEach((link) => {
      const href = link.getAttribute('href') || '';

      if (
        isAuditLink(link) &&
        (href.includes('/pages/contact') || href.startsWith('mailto:') || href === '#' || !href)
      ) {
        link.setAttribute('href', destination);
      }
    });

    getAuditTarget();

    if (window.location.hash === '#audit-form') {
      let attempts = 0;
      const tryScroll = () => {
        if (scrollToAudit() || attempts++ >= 20) return;
        setTimeout(tryScroll, 100);
      };
      tryScroll();
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const link =
        event.target instanceof Element ? event.target.closest('a') : null;

      if (!link || !isAuditLink(link)) return;

      link.setAttribute('href', destination);

      if (window.location.pathname === '/pages/contact') {
        event.preventDefault();

        if (window.location.hash !== '#audit-form') {
          window.location.hash = 'audit-form';
        }

        if (!scrollToAudit()) {
          setTimeout(scrollToAudit, 100);
        }
      }
    },
    true
  );

  window.addEventListener('hashchange', scrollToAudit);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureOscheanoAuditCtas, { once: true });
  } else {
    configureOscheanoAuditCtas();
  }

  document.addEventListener('shopify:section:load', configureOscheanoAuditCtas);
})();


/**
 * OSCHEANO Serving menu persistence:
 * - Bridge the trigger-to-dropdown gap.
 * - Keep the menu open while the pointer is anywhere inside it.
 * - Add a short exit grace period to prevent accidental collapse.
 */
(function () {
  const styleId = 'osx-menu-persistence-styles';

  function installMenuStyles() {
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent =
      '.osx-menu-item:hover::before,.osx-menu-item:focus-within::before,.osx-menu-item.is-hover-open::before{' +
      'content:"";position:absolute;left:-20px;right:-20px;top:100%;height:34px;pointer-events:auto}' +
      '.osx-dropdown{z-index:1200;visibility:hidden}' +
      '.osx-menu-item:hover .osx-dropdown,.osx-menu-item:focus-within .osx-dropdown,.osx-menu-item.is-hover-open .osx-dropdown{' +
      'opacity:1;pointer-events:auto;transform:translate(-50%,0);visibility:visible}' +
      '.osx-menu-item.is-hover-open .osx-menu-trigger{' +
      'color:#67e8f9;text-shadow:0 0 18px rgba(103,232,249,.46),0 0 34px rgba(155,140,255,.24)}';
    document.head.appendChild(style);
  }

  function configureServingMenus() {
    installMenuStyles();

    document.querySelectorAll('.osx-menu-item').forEach((item) => {
      if (item.dataset.osxMenuPersistence === 'active') return;
      item.dataset.osxMenuPersistence = 'active';

      const trigger = item.querySelector('.osx-menu-trigger');
      const dropdown = item.querySelector('.osx-dropdown');
      if (!trigger || !dropdown) return;

      let closeTimer = null;

      const openMenu = () => {
        if (closeTimer) window.clearTimeout(closeTimer);
        closeTimer = null;
        item.classList.add('is-hover-open');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const closeMenu = () => {
        if (closeTimer) window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (item.matches(':hover') || item.matches(':focus-within')) return;
          item.classList.remove('is-hover-open');
          trigger.setAttribute('aria-expanded', 'false');
        }, 240);
      };

      item.addEventListener('pointerenter', openMenu);
      item.addEventListener('pointerleave', closeMenu);
      dropdown.addEventListener('pointerenter', openMenu);
      dropdown.addEventListener('pointerleave', closeMenu);
      trigger.addEventListener('focus', openMenu);
      item.addEventListener('focusout', closeMenu);
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureServingMenus, { once: true });
  } else {
    configureServingMenus();
  }

  document.addEventListener('shopify:section:load', configureServingMenus);
})();


/* Oscheano sitewide finish: restrained depth, motion and accessibility */
(function () {
  var STYLE_ID = 'osx-site-polish-styles';

  function installStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .osx-site-progress {
        position: fixed;
        inset: 0 auto auto 0;
        width: 100%;
        height: 2px;
        z-index: 9999;
        pointer-events: none;
        transform: scaleX(var(--osx-site-progress, 0));
        transform-origin: left center;
        background: linear-gradient(90deg, #67e8f9 0%, #a78bfa 58%, #bef264 100%);
        box-shadow: 0 0 14px rgba(103, 232, 249, .62);
        opacity: .88;
      }

      .osx ::selection {
        color: #fff;
        background: rgba(103, 232, 249, .28);
      }

      :where(.osx a, .osx button, .osx summary, .osx input, .osx textarea, .osx select):focus-visible {
        outline: 2px solid #67e8f9 !important;
        outline-offset: 4px !important;
        box-shadow: 0 0 0 5px rgba(103, 232, 249, .12) !important;
      }

      .page-wrapper,
      .osx-dropdown {
        scrollbar-width: thin;
        scrollbar-color: rgba(103, 232, 249, .42) rgba(5, 7, 13, .48);
      }

      .page-wrapper::-webkit-scrollbar,
      .osx-dropdown::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      .page-wrapper::-webkit-scrollbar-track,
      .osx-dropdown::-webkit-scrollbar-track {
        background: rgba(5, 7, 13, .48);
      }

      .page-wrapper::-webkit-scrollbar-thumb,
      .osx-dropdown::-webkit-scrollbar-thumb {
        border: 2px solid rgba(5, 7, 13, .72);
        border-radius: 999px;
        background: linear-gradient(180deg, rgba(103, 232, 249, .7), rgba(167, 139, 250, .7));
      }

      .osx-nav {
        box-shadow:
          0 18px 48px rgba(0, 0, 0, .22),
          inset 0 -1px rgba(103, 232, 249, .06);
      }

      :where(.osx-btn, .osx-cta, .osx-nav-cta) {
        transition:
          transform .22s cubic-bezier(.2, .8, .2, 1),
          box-shadow .22s ease,
          border-color .22s ease,
          filter .22s ease !important;
      }

      :where(.osx-btn, .osx-cta, .osx-nav-cta):hover {
        filter: saturate(1.08) brightness(1.05);
        box-shadow:
          0 12px 32px rgba(0, 0, 0, .28),
          0 0 24px rgba(103, 232, 249, .12);
      }

      :where(.osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group) {
        transition:
          transform .28s cubic-bezier(.2, .8, .2, 1),
          border-color .28s ease,
          box-shadow .28s ease !important;
      }

      :where(.osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group):hover {
        border-color: rgba(103, 232, 249, .3) !important;
        box-shadow:
          0 18px 50px rgba(0, 0, 0, .22),
          0 0 28px rgba(103, 232, 249, .055);
      }

      .osx-polish-reveal {
        opacity: 0;
        transform: translateY(16px);
        filter: blur(3px);
        transition:
          opacity .66s ease,
          transform .66s cubic-bezier(.2, .8, .2, 1),
          filter .66s ease;
      }

      .osx-polish-reveal.is-visible {
        opacity: 1;
        transform: translateY(0);
        filter: none;
      }

      @media (prefers-reduced-motion: reduce) {
        .osx-site-progress { display: none; }
        .osx-polish-reveal,
        .osx-polish-reveal.is-visible {
          opacity: 1 !important;
          transform: none !important;
          filter: none !important;
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function configurePolish() {
    var root = document.querySelector('.osx');
    if (!root) return;

    installStyles();

    var progress = document.querySelector('.osx-site-progress');
    if (!progress) {
      progress = document.createElement('div');
      progress.className = 'osx-site-progress';
      progress.setAttribute('aria-hidden', 'true');
      document.body.appendChild(progress);
    }

    var scroller = document.querySelector('.page-wrapper');
    var updateProgress = function () {
      var current = scroller ? scroller.scrollTop : window.scrollY;
      var total = scroller
        ? scroller.scrollHeight - scroller.clientHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      var ratio = total > 0 ? Math.min(1, Math.max(0, current / total)) : 0;
      progress.style.setProperty('--osx-site-progress', ratio.toFixed(4));
    };

    if (!progress.dataset.bound) {
      progress.dataset.bound = 'true';
      (scroller || window).addEventListener('scroll', updateProgress, { passive: true });
      window.addEventListener('resize', updateProgress, { passive: true });
    }
    updateProgress();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)) return;

    var observer = window.__osxPolishObserver;
    if (!observer) {
      observer = new IntersectionObserver(function (entries, activeObserver) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          activeObserver.unobserve(entry.target);
        });
      }, { root: scroller || null, rootMargin: '0px 0px -8% 0px', threshold: .08 });
      window.__osxPolishObserver = observer;
    }

    root.querySelectorAll(
      '.osx-section, .osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group'
    ).forEach(function (element) {
      if (element.dataset.osxPolishReady) return;
      element.dataset.osxPolishReady = 'true';

      var rect = element.getBoundingClientRect();
      if (rect.top < window.innerHeight * .78) {
        element.classList.add('is-visible');
        return;
      }

      element.classList.add('osx-polish-reveal');
      observer.observe(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configurePolish, { once: true });
  } else {
    configurePolish();
  }

  document.addEventListener('shopify:section:load', configurePolish);
})();


/* Homepage state tiles: reveal their child city pages on hover/focus */
(function () {
  var CITY_STYLE_ID = 'osx-market-city-hover-styles';

  function installCityStyles() {
    if (document.getElementById(CITY_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = CITY_STYLE_ID;
    style.textContent = `
      .osx-city-hover-section,
      .osx-city-hover-grid {
        overflow: visible !important;
      }

      .osx-market-city-hover {
        position: relative;
        min-width: 0;
        z-index: 1;
      }

      .osx-market-city-hover:hover,
      .osx-market-city-hover:focus-within,
      .osx-market-city-hover.is-open {
        z-index: 120;
      }

      .osx-market-city-parent {
        position: relative;
        z-index: 2;
        width: 100%;
        height: 100%;
      }

      .osx-market-city-parent::after {
        content: '2 cities';
        margin-left: auto;
        padding-left: 10px;
        color: rgba(190, 242, 100, .78);
        font-family: 'Rajdhani', sans-serif;
        font-size: .68rem;
        font-weight: 700;
        letter-spacing: .08em;
        opacity: 0;
        transform: translateX(-4px);
        transition: opacity .2s ease, transform .2s ease;
      }

      .osx-market-city-hover:hover .osx-market-city-parent::after,
      .osx-market-city-hover:focus-within .osx-market-city-parent::after,
      .osx-market-city-hover.is-open .osx-market-city-parent::after {
        opacity: 1;
        transform: translateX(0);
      }

      .osx-market-city-popover {
        position: absolute;
        inset: calc(100% - 1px) 0 auto;
        padding-top: 8px;
        z-index: 100;
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
        transform: translateY(-6px) scale(.985);
        transform-origin: top center;
        transition:
          opacity .2s ease,
          transform .24s cubic-bezier(.2, .8, .2, 1),
          visibility .2s ease;
      }

      .osx-market-city-hover:hover .osx-market-city-popover,
      .osx-market-city-hover:focus-within .osx-market-city-popover,
      .osx-market-city-hover.is-open .osx-market-city-popover {
        visibility: visible;
        opacity: 1;
        pointer-events: auto;
        transform: translateY(0) scale(1);
      }

      .osx-market-city-panel {
        padding: 10px;
        border: 1px solid rgba(103, 232, 249, .26);
        border-radius: 15px;
        background:
          linear-gradient(145deg, rgba(17, 26, 42, .98), rgba(9, 11, 23, .985));
        box-shadow:
          0 22px 54px rgba(0, 0, 0, .52),
          0 0 28px rgba(103, 232, 249, .08),
          inset 0 1px rgba(255, 255, 255, .035);
        backdrop-filter: blur(18px);
      }

      .osx-market-city-label {
        margin: 0 4px 6px;
        color: rgba(185, 198, 218, .68);
        font-family: 'Rajdhani', sans-serif;
        font-size: .68rem;
        font-weight: 700;
        letter-spacing: .12em;
        line-height: 1;
        text-transform: uppercase;
      }

      .osx-market-city-panel .osx-market-city-link {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        width: 100% !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 9px 10px !important;
        border: 0 !important;
        border-radius: 10px !important;
        background: transparent !important;
        box-shadow: none !important;
        color: #edf7ff !important;
        font-family: 'Rajdhani', sans-serif !important;
        font-size: .9rem !important;
        font-weight: 700 !important;
        line-height: 1.15 !important;
        letter-spacing: .01em !important;
        text-decoration: none !important;
        transform: none !important;
      }

      .osx-market-city-panel .osx-market-city-link::after {
        content: '↗';
        color: #67e8f9;
        font-size: .8rem;
        opacity: .72;
        transition: transform .18s ease, opacity .18s ease;
      }

      .osx-market-city-panel .osx-market-city-link:hover,
      .osx-market-city-panel .osx-market-city-link:focus-visible {
        background: linear-gradient(90deg, rgba(103, 232, 249, .11), rgba(167, 139, 250, .09)) !important;
        color: #fff !important;
      }

      .osx-market-city-panel .osx-market-city-link:hover::after,
      .osx-market-city-panel .osx-market-city-link:focus-visible::after {
        opacity: 1;
        transform: translate(2px, -2px);
      }

      .osx-market-city-toggle {
        display: none;
        position: absolute;
        top: 50%;
        right: 9px;
        z-index: 5;
        min-width: 0;
        padding: 5px 8px;
        border: 1px solid rgba(103, 232, 249, .24);
        border-radius: 999px;
        background: rgba(103, 232, 249, .07);
        color: #bdf6ff;
        font-family: 'Rajdhani', sans-serif;
        font-size: .67rem;
        font-weight: 700;
        line-height: 1;
        transform: translateY(-50%);
      }

      @media (hover: none), (pointer: coarse) {
        .osx-market-city-parent {
          padding-right: 64px !important;
        }

        .osx-market-city-parent::after {
          display: none;
        }

        .osx-market-city-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .osx-market-city-popover,
        .osx-market-city-parent::after {
          transition: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function pathnameOf(link) {
    try {
      return new URL(link.href, window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  function buildCityMap(html) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var cityMap = {};

    parsed.querySelectorAll('article').forEach(function (article) {
      var links = Array.from(article.querySelectorAll('a[href]'));
      var parent = links.find(function (link) {
        var path = pathnameOf(link);
        return /^\/pages\/serving-[a-z-]+$/.test(path) && path !== '/pages/serving-markets';
      });

      if (!parent) return;

      var children = links.filter(function (link) {
        return /-seo$/.test(pathnameOf(link));
      }).slice(0, 2);

      if (children.length) {
        cityMap[pathnameOf(parent)] = children.map(function (link) {
          return {
            label: link.textContent.trim(),
            href: pathnameOf(link)
          };
        });
      }
    });

    return cityMap;
  }

  function enhanceStateTiles(cityMap) {
    if (window.location.pathname !== '/') return;

    Object.keys(cityMap).forEach(function (parentPath) {
      document.querySelectorAll('a[href="' + parentPath + '"]').forEach(function (stateLink) {
        if (stateLink.dataset.osxCityHoverReady ||
            stateLink.closest('nav, .osx-dropdown, .osx-market-city-popover')) return;

        var cities = cityMap[parentPath];
        var stateName = stateLink.textContent.trim();
        var grid = stateLink.parentElement;
        if (!grid) return;

        stateLink.dataset.osxCityHoverReady = 'true';
        stateLink.classList.add('osx-market-city-parent');
        grid.classList.add('osx-city-hover-grid');

        var section = stateLink.closest('section, .osx-section');
        if (section) section.classList.add('osx-city-hover-section');

        var wrapper = document.createElement('div');
        wrapper.className = 'osx-market-city-hover';
        stateLink.parentNode.insertBefore(wrapper, stateLink);
        wrapper.appendChild(stateLink);

        var popover = document.createElement('div');
        popover.className = 'osx-market-city-popover';
        popover.id = 'osx-city-popover-' + parentPath.split('/').pop();
        popover.setAttribute('role', 'group');
        popover.setAttribute('aria-label', 'City pages for ' + stateName);

        var panel = document.createElement('div');
        panel.className = 'osx-market-city-panel';

        var label = document.createElement('p');
        label.className = 'osx-market-city-label';
        label.textContent = stateName + ' city pages';
        panel.appendChild(label);

        cities.forEach(function (city) {
          var cityLink = document.createElement('a');
          cityLink.className = 'osx-market-city-link';
          cityLink.href = city.href;
          cityLink.textContent = city.label;
          panel.appendChild(cityLink);
        });

        popover.appendChild(panel);
        wrapper.appendChild(popover);
        stateLink.setAttribute('aria-describedby', popover.id);

        var toggle = document.createElement('button');
        toggle.className = 'osx-market-city-toggle';
        toggle.type = 'button';
        toggle.textContent = 'Cities';
        toggle.setAttribute('aria-label', 'Show city pages for ' + stateName);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', popover.id);
        wrapper.appendChild(toggle);

        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();

          document.querySelectorAll('.osx-market-city-hover.is-open').forEach(function (openWrapper) {
            if (openWrapper !== wrapper) {
              openWrapper.classList.remove('is-open');
              var openToggle = openWrapper.querySelector('.osx-market-city-toggle');
              if (openToggle) openToggle.setAttribute('aria-expanded', 'false');
            }
          });

          var isOpen = wrapper.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', String(isOpen));
        });

        wrapper.addEventListener('keydown', function (event) {
          if (event.key !== 'Escape') return;
          wrapper.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
          stateLink.focus();
        });
      });
    });
  }

  function configureCityHoverMenus() {
    if (window.location.pathname !== '/') return;

    installCityStyles();

    fetch('/pages/serving-markets', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to load city directory');
        return response.text();
      })
      .then(function (html) {
        enhanceStateTiles(buildCityMap(html));
      })
      .catch(function () {
        /* Existing state links remain fully functional if the directory cannot load. */
      });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('.osx-market-city-hover')) return;
    document.querySelectorAll('.osx-market-city-hover.is-open').forEach(function (wrapper) {
      wrapper.classList.remove('is-open');
      var toggle = wrapper.querySelector('.osx-market-city-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureCityHoverMenus, { once: true });
  } else {
    configureCityHoverMenus();
  }

  document.addEventListener('shopify:section:load', configureCityHoverMenus);
})();


/* Oscheano cinematic luxury experience */
(function () {
  var LUXE_STYLE_ID = 'osx-cinematic-luxury-styles';

  function installLuxuryStyles() {
    if (document.getElementById(LUXE_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = LUXE_STYLE_ID;
    style.textContent = `
      .osx-luxe-atmosphere,
      .osx-luxe-aura,
      .osx-luxe-grain {
        position: fixed;
        pointer-events: none;
        user-select: none;
      }

      .osx-luxe-atmosphere {
        inset: -16%;
        z-index: 90;
        opacity: .16;
        background:
          radial-gradient(38% 34% at 18% 30%, rgba(103, 232, 249, .30), transparent 72%),
          radial-gradient(36% 42% at 82% 22%, rgba(167, 139, 250, .28), transparent 74%),
          radial-gradient(30% 36% at 58% 82%, rgba(190, 242, 100, .16), transparent 74%);
        mix-blend-mode: screen;
        animation: osx-luxe-drift 18s ease-in-out infinite alternate;
        will-change: transform;
      }

      .osx-luxe-aura {
        top: 0;
        left: 0;
        width: 520px;
        height: 520px;
        z-index: 92;
        border-radius: 50%;
        opacity: 0;
        background:
          radial-gradient(circle, rgba(103, 232, 249, .13) 0%, rgba(167, 139, 250, .07) 34%, transparent 70%);
        mix-blend-mode: screen;
        transform: translate3d(-600px, -600px, 0);
        transition: opacity .32s ease, transform .09s linear;
        will-change: transform;
      }

      .osx-luxe-aura.is-active {
        opacity: 1;
      }

      .osx-luxe-grain {
        inset: 0;
        z-index: 93;
        opacity: .026;
        background-image: url('data:image/svg+xml,%3Csvg viewBox=%220 0 180 180%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%22.85%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%22.6%22/%3E%3C/svg%3E');
        background-size: 180px 180px;
        mix-blend-mode: soft-light;
      }

      @keyframes osx-luxe-drift {
        0%   { transform: translate3d(-1.5%, -1%, 0) scale(1); }
        50%  { transform: translate3d(2%, 1.5%, 0) scale(1.025); }
        100% { transform: translate3d(-.5%, 2%, 0) scale(1.045); }
      }

      .osx-nav {
        border-bottom-color: rgba(103, 232, 249, .13) !important;
        background:
          linear-gradient(90deg, rgba(5, 10, 19, .92), rgba(13, 9, 26, .91)) !important;
        box-shadow:
          0 18px 64px rgba(0, 0, 0, .34),
          0 1px rgba(103, 232, 249, .08),
          inset 0 -1px rgba(167, 139, 250, .045) !important;
        backdrop-filter: blur(20px) saturate(145%) !important;
      }

      .osx h1 {
        text-shadow:
          0 0 42px rgba(103, 232, 249, .07),
          0 20px 70px rgba(0, 0, 0, .32);
      }

      .osx h2 {
        text-shadow:
          0 0 32px rgba(167, 139, 250, .055),
          0 14px 46px rgba(0, 0, 0, .26);
      }

      :where(.osx-btn, .osx-cta, .osx-nav-cta).osx-luxe-magnetic {
        --osx-magnetic-x: 0px;
        --osx-magnetic-y: 0px;
        position: relative !important;
        isolation: isolate;
        overflow: hidden;
        transform: translate3d(var(--osx-magnetic-x), var(--osx-magnetic-y), 0) !important;
        box-shadow:
          0 14px 40px rgba(0, 0, 0, .28),
          inset 0 1px rgba(255, 255, 255, .13),
          0 0 0 1px rgba(103, 232, 249, .035) !important;
        transition:
          transform .22s cubic-bezier(.2, .8, .2, 1),
          box-shadow .25s ease,
          filter .25s ease !important;
        will-change: transform;
      }

      :where(.osx-btn, .osx-cta, .osx-nav-cta).osx-luxe-magnetic:hover {
        transform: translate3d(var(--osx-magnetic-x), calc(var(--osx-magnetic-y) - 2px), 0) !important;
        box-shadow:
          0 20px 52px rgba(0, 0, 0, .36),
          0 0 32px rgba(103, 232, 249, .12),
          0 0 46px rgba(167, 139, 250, .07),
          inset 0 1px rgba(255, 255, 255, .19) !important;
      }

      .osx-luxe-sheen {
        position: absolute;
        inset: -140% auto -140% -38%;
        width: 24%;
        z-index: -1;
        pointer-events: none;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .38), transparent);
        transform: skewX(-18deg) translateX(-220%);
        transition: transform .72s cubic-bezier(.2, .8, .2, 1);
      }

      .osx-luxe-magnetic:hover .osx-luxe-sheen,
      .osx-luxe-magnetic:focus-visible .osx-luxe-sheen {
        transform: skewX(-18deg) translateX(720%);
      }

      .osx-luxe-ripple {
        position: absolute;
        width: 12px;
        height: 12px;
        z-index: 3;
        border-radius: 50%;
        pointer-events: none;
        background: rgba(255, 255, 255, .42);
        transform: translate(-50%, -50%) scale(0);
        animation: osx-luxe-ripple .64s ease-out forwards;
      }

      @keyframes osx-luxe-ripple {
        to {
          opacity: 0;
          transform: translate(-50%, -50%) scale(18);
        }
      }

      [data-osx-luxe-card] {
        --osx-card-glow-x: 50%;
        --osx-card-glow-y: 50%;
        position: relative;
        isolation: isolate;
      }

      .osx-luxe-card-glow {
        position: absolute;
        inset: 0;
        z-index: 5;
        border-radius: inherit;
        pointer-events: none;
        opacity: 0;
        background:
          radial-gradient(
            260px circle at var(--osx-card-glow-x) var(--osx-card-glow-y),
            rgba(103, 232, 249, .12),
            rgba(167, 139, 250, .045) 38%,
            transparent 70%
          );
        mix-blend-mode: screen;
        transition: opacity .26s ease;
      }

      [data-osx-luxe-card]:hover .osx-luxe-card-glow,
      [data-osx-luxe-card]:focus-within .osx-luxe-card-glow {
        opacity: 1;
      }

      :where(.osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group) {
        border-color: rgba(128, 185, 212, .18) !important;
        box-shadow:
          0 18px 48px rgba(0, 0, 0, .14),
          inset 0 1px rgba(255, 255, 255, .025);
      }

      :where(.osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group):hover {
        border-color: rgba(103, 232, 249, .34) !important;
        box-shadow:
          0 24px 64px rgba(0, 0, 0, .30),
          0 0 34px rgba(103, 232, 249, .075),
          0 0 54px rgba(167, 139, 250, .045),
          inset 0 1px rgba(255, 255, 255, .055) !important;
      }

      .osx-market-city-panel {
        border-color: rgba(103, 232, 249, .34) !important;
        background:
          radial-gradient(circle at 12% 0%, rgba(103, 232, 249, .09), transparent 36%),
          linear-gradient(145deg, rgba(17, 26, 42, .992), rgba(9, 11, 23, .992)) !important;
        box-shadow:
          0 28px 72px rgba(0, 0, 0, .62),
          0 0 36px rgba(103, 232, 249, .09),
          0 0 54px rgba(167, 139, 250, .045),
          inset 0 1px rgba(255, 255, 255, .05) !important;
      }

      @media (max-width: 900px), (hover: none), (pointer: coarse) {
        .osx-luxe-aura {
          display: none;
        }

        :where(.osx-btn, .osx-cta, .osx-nav-cta).osx-luxe-magnetic,
        :where(.osx-btn, .osx-cta, .osx-nav-cta).osx-luxe-magnetic:hover {
          --osx-magnetic-x: 0px !important;
          --osx-magnetic-y: 0px !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .osx-luxe-atmosphere {
          animation: none !important;
        }

        .osx-luxe-aura,
        .osx-luxe-sheen {
          display: none !important;
        }

        .osx-luxe-magnetic {
          transform: none !important;
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function createLayer(className) {
    var layer = document.querySelector('.' + className);
    if (layer) return layer;

    layer = document.createElement('div');
    layer.className = className;
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    return layer;
  }

  function enhanceLuxuryButtons(root) {
    root.querySelectorAll('.osx-btn, .osx-cta, .osx-nav-cta').forEach(function (button) {
      if (button.dataset.osxLuxeButton) return;
      button.dataset.osxLuxeButton = 'true';
      button.classList.add('osx-luxe-magnetic');

      var sheen = document.createElement('span');
      sheen.className = 'osx-luxe-sheen';
      sheen.setAttribute('aria-hidden', 'true');
      button.appendChild(sheen);

      button.addEventListener('pointermove', function (event) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        var rect = button.getBoundingClientRect();
        var x = (event.clientX - rect.left - rect.width / 2) * .11;
        var y = (event.clientY - rect.top - rect.height / 2) * .13;
        button.style.setProperty('--osx-magnetic-x', x.toFixed(2) + 'px');
        button.style.setProperty('--osx-magnetic-y', y.toFixed(2) + 'px');
      });

      button.addEventListener('pointerleave', function () {
        button.style.setProperty('--osx-magnetic-x', '0px');
        button.style.setProperty('--osx-magnetic-y', '0px');
      });

      button.addEventListener('click', function (event) {
        var rect = button.getBoundingClientRect();
        var ripple = document.createElement('span');
        ripple.className = 'osx-luxe-ripple';
        ripple.style.left = (event.clientX ? event.clientX - rect.left : rect.width / 2) + 'px';
        ripple.style.top = (event.clientY ? event.clientY - rect.top : rect.height / 2) + 'px';
        ripple.setAttribute('aria-hidden', 'true');
        button.appendChild(ripple);
        window.setTimeout(function () { ripple.remove(); }, 700);
      });
    });
  }

  function enhanceLuxuryCards(root) {
    var cards = Array.from(root.querySelectorAll(
      '.osx-card, .osx-row, .osx-market, .osx-state, .osx-child-directory__group'
    )).slice(0, 80);

    cards.forEach(function (card) {
      if (card.dataset.osxLuxeCard) return;
      card.dataset.osxLuxeCard = 'true';
      card.setAttribute('data-osx-luxe-card', '');

      var glow = document.createElement('span');
      glow.className = 'osx-luxe-card-glow';
      glow.setAttribute('aria-hidden', 'true');
      card.appendChild(glow);

      card.addEventListener('pointermove', function (event) {
        var rect = card.getBoundingClientRect();
        card.style.setProperty('--osx-card-glow-x', (event.clientX - rect.left) + 'px');
        card.style.setProperty('--osx-card-glow-y', (event.clientY - rect.top) + 'px');
      });
    });
  }

  function configureLuxuryExperience() {
    var root = document.querySelector('.osx');
    if (!root) return;

    installLuxuryStyles();
    createLayer('osx-luxe-atmosphere');
    createLayer('osx-luxe-grain');

    var aura = createLayer('osx-luxe-aura');
    if (!document.documentElement.dataset.osxLuxePointer) {
      document.documentElement.dataset.osxLuxePointer = 'true';

      document.addEventListener('pointermove', function (event) {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        aura.classList.add('is-active');
        aura.style.transform = 'translate3d(' +
          (event.clientX - 260) + 'px,' +
          (event.clientY - 260) + 'px,0)';
      }, { passive: true });

      document.addEventListener('pointerleave', function () {
        aura.classList.remove('is-active');
      });
    }

    enhanceLuxuryButtons(root);
    enhanceLuxuryCards(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureLuxuryExperience, { once: true });
  } else {
    configureLuxuryExperience();
  }

  document.addEventListener('shopify:section:load', configureLuxuryExperience);
})();


/* Orbit cards: synchronized oscillating color rims */
(function () {
  var RIM_STYLE_ID = 'osx-orbit-rim-styles';

  function installRimStyles() {
    if (document.getElementById(RIM_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = RIM_STYLE_ID;
    style.textContent = `
      @property --osx-rim-angle {
        syntax: '<angle>';
        inherits: false;
        initial-value: 0deg;
      }

      [aria-label='Interactive website system orbit'] .osx-panel {
        position: absolute;
        isolation: isolate;
        overflow: visible !important;
      }

      .osx-orbit-rim {
        --osx-rim-angle: 0deg;
        position: absolute;
        inset: -1.5px;
        z-index: 7;
        padding: 1.5px;
        border-radius: inherit;
        pointer-events: none;
        opacity: .9;
        background:
          conic-gradient(
            from var(--osx-rim-angle),
            #67e8f9 0deg,
            rgba(103, 232, 249, .12) 38deg,
            rgba(103, 232, 249, 0) 74deg,
            #a78bfa 124deg,
            rgba(167, 139, 250, .12) 165deg,
            rgba(167, 139, 250, 0) 205deg,
            #bef264 252deg,
            rgba(190, 242, 100, .12) 298deg,
            rgba(190, 242, 100, 0) 332deg,
            #67e8f9 360deg
          );
        -webkit-mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        mask-composite: exclude;
        filter:
          drop-shadow(0 0 4px rgba(103, 232, 249, .55))
          drop-shadow(0 0 8px rgba(167, 139, 250, .24));
        animation:
          osx-orbit-rim-travel 4.2s linear infinite,
          osx-orbit-rim-pulse 2.1s ease-in-out infinite alternate;
        will-change: --osx-rim-angle, opacity, filter;
      }

      .osx-orbit-rim::after {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: inherit;
        border: 1px solid rgba(255, 255, 255, .06);
        box-shadow:
          0 0 14px rgba(103, 232, 249, .10),
          inset 0 0 12px rgba(167, 139, 250, .05);
      }

      [aria-label='Interactive website system orbit'] .osx-panel:hover .osx-orbit-rim,
      [aria-label='Interactive website system orbit'] .osx-panel:focus-visible .osx-orbit-rim,
      [aria-label='Interactive website system orbit'] .osx-panel:focus-within .osx-orbit-rim {
        opacity: 1;
        filter:
          drop-shadow(0 0 5px rgba(103, 232, 249, .76))
          drop-shadow(0 0 12px rgba(167, 139, 250, .38))
          drop-shadow(0 0 16px rgba(190, 242, 100, .18));
        animation-play-state: running;
      }

      @keyframes osx-orbit-rim-travel {
        to { --osx-rim-angle: 360deg; }
      }

      @keyframes osx-orbit-rim-pulse {
        0% {
          opacity: .68;
          filter:
            drop-shadow(0 0 3px rgba(103, 232, 249, .38))
            drop-shadow(0 0 7px rgba(167, 139, 250, .17));
        }
        100% {
          opacity: 1;
          filter:
            drop-shadow(0 0 5px rgba(103, 232, 249, .72))
            drop-shadow(0 0 11px rgba(167, 139, 250, .32))
            drop-shadow(0 0 13px rgba(190, 242, 100, .15));
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .osx-orbit-rim {
          --osx-rim-angle: 34deg;
          animation: none !important;
          opacity: .88;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function configureOrbitRims() {
    var orbit = document.querySelector('[aria-label="Interactive website system orbit"]');
    if (!orbit) return;

    installRimStyles();

    orbit.querySelectorAll('.osx-panel').forEach(function (panel, index) {
      if (panel.dataset.osxOrbitRim) return;
      panel.dataset.osxOrbitRim = 'true';

      var rim = document.createElement('span');
      rim.className = 'osx-orbit-rim';
      rim.setAttribute('aria-hidden', 'true');
      rim.style.animationDelay = (-index * 1.4) + 's, ' + (-index * .7) + 's';
      panel.appendChild(rim);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureOrbitRims, { once: true });
  } else {
    configureOrbitRims();
  }

  document.addEventListener('shopify:section:load', configureOrbitRims);
})();


/* Oscheano website + SEO investment calculator */
(function () {
  var CALC_STYLE_ID = 'osx-investment-calculator-styles';

  function installCalculatorStyles() {
    if (document.getElementById(CALC_STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = CALC_STYLE_ID;
    style.textContent = `
      .osx-investment-calculator {
        position: relative;
        isolation: isolate;
        overflow: hidden;
        padding: clamp(78px, 9vw, 130px) clamp(22px, 5vw, 72px);
        border-top: 1px solid rgba(103, 232, 249, .10);
        border-bottom: 1px solid rgba(167, 139, 250, .10);
        background:
          radial-gradient(circle at 8% 18%, rgba(103, 232, 249, .09), transparent 31%),
          radial-gradient(circle at 92% 72%, rgba(167, 139, 250, .09), transparent 34%),
          linear-gradient(180deg, rgba(7, 11, 20, .98), rgba(5, 7, 13, .995));
      }

      .osx-investment-calculator::before {
        content: '';
        position: absolute;
        inset: 0;
        z-index: -1;
        opacity: .22;
        background-image:
          linear-gradient(rgba(103, 232, 249, .045) 1px, transparent 1px),
          linear-gradient(90deg, rgba(103, 232, 249, .045) 1px, transparent 1px);
        background-size: 72px 72px;
        mask-image: linear-gradient(to bottom, transparent, #000 24%, #000 78%, transparent);
      }

      .osx-calc-wrap {
        width: min(1180px, 100%);
        margin: 0 auto;
      }

      .osx-calc-heading {
        max-width: 900px;
        margin-bottom: clamp(36px, 5vw, 62px);
      }

      .osx-calc-kicker,
      .osx-calc-result-kicker {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        margin: 0 0 18px;
        color: #bdf6ff;
        font-family: 'Rajdhani', sans-serif;
        font-size: .78rem;
        font-weight: 700;
        letter-spacing: .18em;
        line-height: 1;
        text-transform: uppercase;
      }

      .osx-calc-kicker::before,
      .osx-calc-result-kicker::before {
        content: '';
        width: 25px;
        height: 1px;
        background: linear-gradient(90deg, #67e8f9, #a78bfa);
        box-shadow: 0 0 9px rgba(103, 232, 249, .45);
      }

      .osx-calc-heading h2 {
        max-width: 880px;
        margin: 0;
        color: #f6fbff;
        font-family: 'Chakra Petch', sans-serif;
        font-size: clamp(2.35rem, 5.4vw, 5rem);
        font-weight: 700;
        letter-spacing: -.045em;
        line-height: .98;
      }

      .osx-calc-heading > p:last-child {
        max-width: 760px;
        margin: 24px 0 0;
        color: rgba(211, 222, 238, .74);
        font-family: 'Rajdhani', sans-serif;
        font-size: clamp(1rem, 1.6vw, 1.2rem);
        font-weight: 500;
        line-height: 1.65;
      }

      .osx-calc-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.08fr) minmax(360px, .92fr);
        gap: clamp(24px, 4vw, 48px);
        align-items: stretch;
      }

      .osx-calc-controls {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }

      .osx-calc-control {
        min-width: 0;
        padding: 21px 20px 19px;
        border: 1px solid rgba(128, 185, 212, .17);
        border-radius: 17px;
        background:
          linear-gradient(145deg, rgba(18, 25, 40, .78), rgba(10, 13, 25, .90));
        box-shadow:
          0 18px 44px rgba(0, 0, 0, .17),
          inset 0 1px rgba(255, 255, 255, .025);
        transition: border-color .24s ease, transform .24s ease, box-shadow .24s ease;
      }

      .osx-calc-control:hover,
      .osx-calc-control:focus-within {
        border-color: rgba(103, 232, 249, .34);
        transform: translateY(-2px);
        box-shadow:
          0 22px 50px rgba(0, 0, 0, .24),
          0 0 25px rgba(103, 232, 249, .055);
      }

      .osx-calc-label-row {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        align-items: baseline;
        margin-bottom: 16px;
      }

      .osx-calc-label-row label {
        color: rgba(230, 239, 250, .82);
        font-family: 'Rajdhani', sans-serif;
        font-size: .86rem;
        font-weight: 700;
        letter-spacing: .035em;
        line-height: 1.2;
      }

      .osx-calc-label-row output {
        flex: none;
        color: #fff;
        font-family: 'Chakra Petch', sans-serif;
        font-size: .96rem;
        font-weight: 700;
        letter-spacing: -.025em;
      }

      .osx-calc-control input[type='range'] {
        width: 100%;
        height: 5px;
        margin: 0;
        border: 0;
        border-radius: 999px;
        outline: none;
        appearance: none;
        background: linear-gradient(90deg, #67e8f9 0 var(--osx-range-fill, 50%), rgba(116, 135, 165, .18) var(--osx-range-fill, 50%) 100%);
        cursor: pointer;
      }

      .osx-calc-control input[type='range']::-webkit-slider-thumb {
        width: 17px;
        height: 17px;
        border: 2px solid #dffbff;
        border-radius: 50%;
        appearance: none;
        background: linear-gradient(145deg, #67e8f9, #a78bfa);
        box-shadow:
          0 0 0 5px rgba(103, 232, 249, .09),
          0 0 16px rgba(103, 232, 249, .40);
      }

      .osx-calc-control input[type='range']::-moz-range-thumb {
        width: 15px;
        height: 15px;
        border: 2px solid #dffbff;
        border-radius: 50%;
        background: linear-gradient(145deg, #67e8f9, #a78bfa);
        box-shadow: 0 0 16px rgba(103, 232, 249, .40);
      }

      .osx-calc-control small {
        display: block;
        margin-top: 12px;
        color: rgba(170, 185, 207, .56);
        font-family: 'Rajdhani', sans-serif;
        font-size: .75rem;
        font-weight: 500;
        line-height: 1.35;
      }

      .osx-calc-result {
        position: relative;
        display: flex;
        flex-direction: column;
        min-width: 0;
        padding: clamp(26px, 3vw, 38px);
        border: 1px solid rgba(103, 232, 249, .28);
        border-radius: 25px;
        overflow: hidden;
        background:
          radial-gradient(circle at 12% 0%, rgba(103, 232, 249, .11), transparent 37%),
          radial-gradient(circle at 92% 100%, rgba(167, 139, 250, .11), transparent 39%),
          linear-gradient(150deg, rgba(17, 26, 42, .97), rgba(8, 10, 21, .99));
        box-shadow:
          0 32px 88px rgba(0, 0, 0, .44),
          0 0 46px rgba(103, 232, 249, .075),
          inset 0 1px rgba(255, 255, 255, .055);
      }

      .osx-calc-result::before {
        content: '';
        position: absolute;
        inset: 0 0 auto;
        height: 2px;
        background: linear-gradient(90deg, #67e8f9, #a78bfa 56%, #bef264);
        box-shadow: 0 0 18px rgba(103, 232, 249, .55);
      }

      .osx-calc-main-value {
        margin: 3px 0 7px;
        color: #fff;
        font-family: 'Chakra Petch', sans-serif;
        font-size: clamp(2.75rem, 5vw, 5.25rem);
        font-weight: 700;
        letter-spacing: -.065em;
        line-height: .95;
        text-shadow:
          0 0 34px rgba(103, 232, 249, .14),
          0 18px 52px rgba(0, 0, 0, .34);
      }

      .osx-calc-main-label {
        margin: 0 0 26px;
        color: rgba(205, 220, 238, .68);
        font-family: 'Rajdhani', sans-serif;
        font-size: .98rem;
        font-weight: 600;
        line-height: 1.4;
      }

      .osx-calc-metrics {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 9px;
        margin-bottom: 24px;
      }

      .osx-calc-metric {
        padding: 14px 12px;
        border: 1px solid rgba(139, 164, 195, .14);
        border-radius: 13px;
        background: rgba(5, 8, 17, .44);
      }

      .osx-calc-metric strong {
        display: block;
        color: #f7fbff;
        font-family: 'Chakra Petch', sans-serif;
        font-size: clamp(.96rem, 1.7vw, 1.28rem);
        font-weight: 700;
        letter-spacing: -.03em;
        line-height: 1.05;
      }

      .osx-calc-metric span {
        display: block;
        margin-top: 6px;
        color: rgba(178, 194, 216, .56);
        font-family: 'Rajdhani', sans-serif;
        font-size: .68rem;
        font-weight: 700;
        letter-spacing: .07em;
        line-height: 1.2;
        text-transform: uppercase;
      }

      .osx-calc-summary {
        margin: 0 0 18px;
        color: rgba(225, 235, 247, .78);
        font-family: 'Rajdhani', sans-serif;
        font-size: 1rem;
        font-weight: 500;
        line-height: 1.55;
      }

      .osx-calc-summary strong {
        color: #bef264;
        font-weight: 700;
      }

      .osx-calc-partner {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: auto 0 20px;
        padding-top: 18px;
        border-top: 1px solid rgba(103, 232, 249, .10);
        color: rgba(188, 205, 226, .62);
        font-family: 'Rajdhani', sans-serif;
        font-size: .76rem;
        font-weight: 700;
        letter-spacing: .075em;
        line-height: 1.3;
        text-transform: uppercase;
      }

      .osx-calc-partner::before {
        content: '';
        width: 8px;
        height: 8px;
        flex: none;
        border-radius: 50%;
        background: #bef264;
        box-shadow: 0 0 14px rgba(190, 242, 100, .60);
      }

      .osx-calc-result .osx-calc-cta {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
        padding: 13px 20px;
        border: 1px solid rgba(103, 232, 249, .44);
        border-radius: 999px;
        background: linear-gradient(110deg, rgba(103, 232, 249, .20), rgba(167, 139, 250, .23));
        color: #fff;
        font-family: 'Rajdhani', sans-serif;
        font-size: .9rem;
        font-weight: 700;
        text-decoration: none;
        box-shadow:
          0 14px 34px rgba(0, 0, 0, .28),
          0 0 22px rgba(103, 232, 249, .09);
      }

      .osx-calc-disclaimer {
        max-width: 1000px;
        margin: 24px 0 0;
        color: rgba(155, 173, 198, .48);
        font-family: 'Rajdhani', sans-serif;
        font-size: .76rem;
        font-weight: 500;
        line-height: 1.5;
      }

      @media (max-width: 920px) {
        .osx-calc-grid {
          grid-template-columns: 1fr;
        }

        .osx-calc-result {
          min-height: 0;
        }
      }

      @media (max-width: 620px) {
        .osx-investment-calculator {
          padding-inline: 17px;
        }

        .osx-calc-controls {
          grid-template-columns: 1fr;
        }

        .osx-calc-metrics {
          grid-template-columns: 1fr;
        }

        .osx-calc-metric {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        }

        .osx-calc-metric span {
          margin-top: 0;
          text-align: right;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .osx-calc-control {
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function formatMoney(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(Math.round(value));
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(Math.round(value));
  }

  function configureInvestmentCalculator() {
    if (window.location.pathname !== '/pages/our-services' ||
        document.getElementById('osx-investment-calculator')) return;

    var root = document.querySelector('.osx');
    if (!root) return;

    installCalculatorStyles();

    var section = document.createElement('section');
    section.id = 'osx-investment-calculator';
    section.className = 'osx-investment-calculator';
    section.setAttribute('aria-labelledby', 'osx-calc-title');
    section.innerHTML = `
      <div class="osx-calc-wrap">
        <header class="osx-calc-heading">
          <p class="osx-calc-kicker">Website + SEO growth scenario</p>
          <h2 id="osx-calc-title">What could a stronger website investment return?</h2>
          <p>Use your real business numbers to model how better website conversion and increased qualified traffic could affect revenue over the next 12 months.</p>
        </header>

        <div class="osx-calc-grid">
          <div class="osx-calc-controls">
            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-investment">Website + SEO investment</label>
                <output for="osx-calc-investment" data-calc-display="investment">$5,000</output>
              </div>
              <input id="osx-calc-investment" data-calc-input="investment" type="range" min="2500" max="25000" step="500" value="5000">
              <small>Estimated total investment in the website and initial growth foundation.</small>
            </div>

            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-visitors">Monthly qualified visitors</label>
                <output for="osx-calc-visitors" data-calc-display="visitors">450</output>
              </div>
              <input id="osx-calc-visitors" data-calc-input="visitors" type="range" min="100" max="5000" step="50" value="450">
              <small>Approximate visitors who could realistically become customers.</small>
            </div>

            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-value">Average customer value</label>
                <output for="osx-calc-value" data-calc-display="clientValue">$1,000</output>
              </div>
              <input id="osx-calc-value" data-calc-input="clientValue" type="range" min="250" max="20000" step="250" value="1000">
              <small>Average gross revenue generated by one new customer.</small>
            </div>

            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-current-rate">Current inquiry rate</label>
                <output for="osx-calc-current-rate" data-calc-display="currentRate">1.0%</output>
              </div>
              <input id="osx-calc-current-rate" data-calc-input="currentRate" type="range" min="0.2" max="8" step="0.1" value="1">
              <small>Percentage of qualified visitors currently becoming customers.</small>
            </div>

            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-future-rate">Improved inquiry rate</label>
                <output for="osx-calc-future-rate" data-calc-display="futureRate">1.3%</output>
              </div>
              <input id="osx-calc-future-rate" data-calc-input="futureRate" type="range" min="0.3" max="10" step="0.1" value="1.3">
              <small>Planning assumption after stronger messaging, trust, and contact flow.</small>
            </div>

            <div class="osx-calc-control">
              <div class="osx-calc-label-row">
                <label for="osx-calc-traffic-growth">Projected traffic growth</label>
                <output for="osx-calc-traffic-growth" data-calc-display="trafficGrowth">20%</output>
              </div>
              <input id="osx-calc-traffic-growth" data-calc-input="trafficGrowth" type="range" min="0" max="150" step="5" value="20">
              <small>Potential qualified-traffic lift from stronger SEO and market visibility.</small>
            </div>
          </div>

          <aside class="osx-calc-result" aria-live="polite">
            <p class="osx-calc-result-kicker">Projected 12-month opportunity</p>
            <div class="osx-calc-main-value" data-calc-result="additionalRevenue">$30,240</div>
            <p class="osx-calc-main-label">Potential additional gross revenue</p>

            <div class="osx-calc-metrics">
              <div class="osx-calc-metric">
                <strong data-calc-result="multiple">6.0×</strong>
                <span>Revenue per $1 invested</span>
              </div>
              <div class="osx-calc-metric">
                <strong data-calc-result="netGain">$25,240</strong>
                <span>Net opportunity</span>
              </div>
              <div class="osx-calc-metric">
                <strong data-calc-result="payback">2.0 mo.</strong>
                <span>Modeled payback</span>
              </div>
            </div>

            <p class="osx-calc-summary" data-calc-result="summary">At these assumptions, a <strong>$5,000</strong> investment supports a potential <strong>$30,240</strong> in additional 12-month gross revenue.</p>

            <p class="osx-calc-partner">Oscheano website conversion × Soltis SEO growth strategy</p>
            <a class="osx-calc-cta osx-btn osx-cta" href="/pages/contact#audit-form">Request a custom revenue scenario</a>
          </aside>
        </div>

        <p class="osx-calc-disclaimer">Planning estimate only—not a promise or guarantee. Results depend on market demand, competition, search performance, traffic quality, close rate, customer value, sales follow-up, margins, and execution. Figures show modeled gross revenue opportunity, not profit. Use verified analytics and business data for a decision-grade forecast.</p>
      </div>
    `;

    var footer = root.querySelector('.osx-footer');
    if (footer) {
      root.insertBefore(section, footer);
    } else {
      root.appendChild(section);
    }

    var inputs = {};
    section.querySelectorAll('[data-calc-input]').forEach(function (input) {
      inputs[input.dataset.calcInput] = input;
    });

    function paintRange(input) {
      var min = Number(input.min);
      var max = Number(input.max);
      var value = Number(input.value);
      var fill = max > min ? ((value - min) / (max - min)) * 100 : 0;
      input.style.setProperty('--osx-range-fill', fill.toFixed(2) + '%');
    }

    function updateCalculator() {
      var investment = Number(inputs.investment.value);
      var visitors = Number(inputs.visitors.value);
      var clientValue = Number(inputs.clientValue.value);
      var currentRate = Number(inputs.currentRate.value) / 100;
      var futureRate = Number(inputs.futureRate.value) / 100;
      var trafficGrowth = Number(inputs.trafficGrowth.value) / 100;

      var currentRevenue = visitors * 12 * currentRate * clientValue;
      var projectedVisitors = visitors * (1 + trafficGrowth);
      var projectedRevenue = projectedVisitors * 12 * futureRate * clientValue;
      var additionalRevenue = Math.max(0, projectedRevenue - currentRevenue);
      var netGain = additionalRevenue - investment;
      var multiple = investment > 0 ? additionalRevenue / investment : 0;
      var payback = additionalRevenue > 0 ? investment / (additionalRevenue / 12) : 0;

      section.querySelector('[data-calc-display="investment"]').textContent = formatMoney(investment);
      section.querySelector('[data-calc-display="visitors"]').textContent = formatNumber(visitors);
      section.querySelector('[data-calc-display="clientValue"]').textContent = formatMoney(clientValue);
      section.querySelector('[data-calc-display="currentRate"]').textContent = (currentRate * 100).toFixed(1) + '%';
      section.querySelector('[data-calc-display="futureRate"]').textContent = (futureRate * 100).toFixed(1) + '%';
      section.querySelector('[data-calc-display="trafficGrowth"]').textContent = Math.round(trafficGrowth * 100) + '%';

      section.querySelector('[data-calc-result="additionalRevenue"]').textContent = formatMoney(additionalRevenue);
      section.querySelector('[data-calc-result="multiple"]').textContent = multiple.toFixed(1) + '×';
      section.querySelector('[data-calc-result="netGain"]').textContent = formatMoney(netGain);
      section.querySelector('[data-calc-result="payback"]').textContent = additionalRevenue > 0 ? payback.toFixed(1) + ' mo.' : '—';
      section.querySelector('[data-calc-result="summary"]').innerHTML =
        'At these assumptions, a <strong>' + formatMoney(investment) +
        '</strong> investment supports a potential <strong>' +
        formatMoney(additionalRevenue) +
        '</strong> in additional 12-month gross revenue.';

      Object.keys(inputs).forEach(function (key) {
        paintRange(inputs[key]);
      });
    }

    section.addEventListener('input', updateCalculator);
    section.addEventListener('change', updateCalculator);
    updateCalculator();

    window.requestAnimationFrame(function () {
      document.dispatchEvent(new Event('shopify:section:load'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', configureInvestmentCalculator, { once: true });
  } else {
    configureInvestmentCalculator();
  }

  document.addEventListener('shopify:section:load', configureInvestmentCalculator);
})();


/* Homepage entry point for the investment calculator */
(function () {
  function addCalculatorEntryPoint() {
    if (window.location.pathname !== '/') return;

    var existing = document.querySelector('[data-osx-calculator-entry]');
    if (existing) return;

    var target = Array.from(document.querySelectorAll('a')).find(function (link) {
      return link.textContent.trim() === 'View service markets';
    });
    if (!target || !target.parentNode) return;

    var link = document.createElement('a');
    link.href = '/pages/our-services#osx-investment-calculator';
    link.className = target.className;
    link.textContent = 'Estimate website ROI';
    link.dataset.osxCalculatorEntry = 'true';
    link.setAttribute('aria-label', 'Estimate website and SEO return on investment');

    target.parentNode.insertBefore(link, target.nextSibling);

    window.requestAnimationFrame(function () {
      document.dispatchEvent(new Event('shopify:section:load'));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addCalculatorEntryPoint, { once: true });
  } else {
    addCalculatorEntryPoint();
  }

  document.addEventListener('shopify:section:load', addCalculatorEntryPoint);
})();


/* Hero orbit alignment: lift the full rotating-card stage */
(function () {
  var id = 'osx-orbit-lift-styles';
  if (document.getElementById(id)) return;

  var style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @media (min-width: 901px) {
      [aria-label='Interactive website system orbit'].osx-orbit {
        transform: translateY(-28px) !important;
      }
    }

    @media (min-width: 621px) and (max-width: 900px) {
      [aria-label='Interactive website system orbit'].osx-orbit {
        transform: translateY(-14px) !important;
      }
    }

    @media (max-width: 620px) {
      [aria-label='Interactive website system orbit'].osx-orbit {
        transform: none !important;
      }
    }
  `;
  document.head.appendChild(style);
})();


/* Final desktop alignment against the Websites headline baseline */
(function () {
  var id = 'osx-orbit-baseline-alignment';
  if (document.getElementById(id)) return;

  var style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @media (min-width: 901px) {
      [aria-label='Interactive website system orbit'].osx-orbit {
        transform: translateY(clamp(-82px, -5vw, -64px)) !important;
      }
    }

    @media (min-width: 621px) and (max-width: 900px) {
      [aria-label='Interactive website system orbit'].osx-orbit {
        transform: translateY(-30px) !important;
      }
    }
  `;
  document.head.appendChild(style);
})();

/* osx-mobile-polish-v1: phone-only proportion and layout refinement */
(function () {
  var id = 'osx-mobile-polish-v1';
  if (document.getElementById(id)) return;
  var style = document.createElement('style');
  style.id = id;
  style.textContent = `
@media (max-width: 620px) {
  html, body { overflow-x: hidden !important; }
  .osx .osx-wrap { padding-left: 16px !important; padding-right: 16px !important; }

  .osx .osx-nav .osx-wrap { min-height: 64px !important; gap: 10px !important; }
  .osx .osx-logo { gap: 8px !important; font-size: .9rem !important; }
  .osx .osx-logo-mark { width: 36px !important; height: 36px !important; }
  .osx .osx-cta { min-height: 44px !important; padding: 0 15px !important; font-size: .83rem !important; }

  .osx .osx-hero { padding-top: 46px !important; padding-bottom: 42px !important; }
  .osx .osx-city-hero { padding-top: 48px !important; padding-bottom: 48px !important; }
  .osx .osx-hero-grid,
  .osx .osx-page-hero-grid { gap: 28px !important; }
  .osx .osx-hero-grid > *,
  .osx .osx-page-hero-grid > * { min-width: 0 !important; max-width: 100% !important; }

  .osx .osx-hero h1,
  .osx .osx-city-hero h1 {
    font-size: clamp(2.15rem, 10.4vw, 2.5rem) !important;
    line-height: .96 !important;
    letter-spacing: -.035em !important;
    max-width: 100% !important;
    overflow-wrap: break-word !important;
  }
  .osx .osx-section h2 {
    font-size: clamp(1.9rem, 8.5vw, 2.28rem) !important;
    line-height: 1.02 !important;
    letter-spacing: -.025em !important;
  }
  .osx .osx-kicker {
    display: block !important;
    width: fit-content !important;
    max-width: 100% !important;
    padding: 9px 12px !important;
    font-size: .72rem !important;
    line-height: 1.45 !important;
    letter-spacing: .16em !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }
  .osx .osx-lede { margin-top: 18px !important; font-size: 1rem !important; line-height: 1.58 !important; }
  .osx .osx-section p { line-height: 1.62 !important; }
  .osx .osx-section { padding-top: 56px !important; padding-bottom: 56px !important; }

  .osx .osx-actions { gap: 9px !important; margin-top: 22px !important; }
  .osx .osx-btn { min-height: 50px !important; padding: 0 15px !important; font-size: .9rem !important; }
  .osx .osx-metrics { gap: 8px !important; margin-top: 22px !important; }
  .osx .osx-metric { min-height: 0 !important; padding: 14px !important; gap: 11px !important; }
  .osx .osx-metric b { font-size: 1rem !important; }
  .osx .osx-metric span { font-size: .9rem !important; line-height: 1.4 !important; }

  .osx .osx-orbit {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: 218px !important;
    min-height: 218px !important;
    margin: 18px 0 0 !important;
    transform: none !important;
    overflow: hidden !important;
    border-radius: 24px !important;
  }
  .osx .osx-nebula,
  .osx .osx-constellation { display: none !important; }
  .osx .osx-orbit-track {
    position: relative !important;
    inset: auto !important;
    display: flex !important;
    align-items: stretch !important;
    gap: 10px !important;
    width: 100% !important;
    height: 100% !important;
    padding: 12px 2px 16px !important;
    overflow-x: auto !important;
    overflow-y: hidden !important;
    scroll-snap-type: x mandatory !important;
    scrollbar-width: none !important;
  }
  .osx .osx-orbit-track::-webkit-scrollbar { display: none !important; }
  .osx .osx-orbit .osx-panel {
    position: relative !important;
    inset: auto !important;
    left: auto !important;
    top: auto !important;
    flex: 0 0 82% !important;
    width: auto !important;
    max-width: 82% !important;
    min-height: 188px !important;
    height: auto !important;
    padding: 20px !important;
    transform: none !important;
    opacity: 1 !important;
    scroll-snap-align: center !important;
    scroll-snap-stop: always !important;
  }
  .osx .osx-orbit .osx-panel-note {
    display: block !important;
    max-height: none !important;
    margin-top: 10px !important;
    opacity: 1 !important;
    visibility: visible !important;
    transform: none !important;
    font-size: .86rem !important;
    line-height: 1.45 !important;
  }
  .osx .osx-orbit .osx-panel strong { font-size: 1.05rem !important; }

  .osx .osx-cards { gap: 12px !important; margin-top: 24px !important; }
  .osx .osx-card { padding: 20px !important; border-radius: 20px !important; }
  .osx .osx-card h3 { font-size: 1.08rem !important; line-height: 1.18 !important; }
  .osx .osx-card p { font-size: .94rem !important; line-height: 1.55 !important; }
  .osx .osx-row { grid-template-columns: 46px minmax(0, 1fr) !important; gap: 12px !important; padding: 18px !important; }
  .osx .osx-row h3 { font-size: 1.05rem !important; line-height: 1.2 !important; }
  .osx .osx-row p { font-size: .92rem !important; line-height: 1.52 !important; }
  .osx .osx-dot { width: 44px !important; height: 44px !important; }
  .osx .osx-feature-band { grid-template-columns: 1fr !important; gap: 9px !important; }

  .osx section:has(.osx-state-cloud) .osx-actions {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 8px !important;
  }
  .osx section:has(.osx-state-cloud) .osx-actions .osx-btn { width: 100% !important; min-width: 0 !important; }
  .osx .osx-state-cloud {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 8px !important;
    margin-top: 20px !important;
  }
  .osx .osx-state-cloud a {
    min-width: 0 !important;
    min-height: 50px !important;
    padding: 11px 10px !important;
    font-size: .82rem !important;
    line-height: 1.2 !important;
    border-radius: 14px !important;
  }

  .osx .osx-page-visual { min-height: 255px !important; height: 255px !important; border-radius: 22px !important; }
  .osx .osx-visual-card { left: 14px !important; right: 14px !important; bottom: 14px !important; padding: 16px !important; }
  .osx .osx-mini-map { grid-template-columns: 1fr !important; gap: 10px !important; }

  .osx .osx-form-card { padding: 20px !important; }
  .osx .osx-form-card input,
  .osx .osx-form-card textarea { font-size: 16px !important; padding: 13px !important; }
  .osx .osx-form-card textarea { min-height: 118px !important; }
  .osx .osx-footer { padding-top: 34px !important; padding-bottom: 34px !important; }
  .osx .osx-footer p { font-size: .88rem !important; line-height: 1.5 !important; }
  .osx-top-toggle { right: 14px !important; bottom: 14px !important; width: 48px !important; height: 48px !important; }
}

@media (max-width: 360px) {
  .osx .osx-wrap { padding-left: 14px !important; padding-right: 14px !important; }
  .osx .osx-logo { font-size: .82rem !important; }
  .osx .osx-logo-mark { width: 32px !important; height: 32px !important; }
  .osx .osx-cta { padding: 0 12px !important; font-size: .78rem !important; }
  .osx .osx-hero h1,
  .osx .osx-city-hero h1 { font-size: 2.05rem !important; }
  .osx .osx-state-cloud a { font-size: .78rem !important; }
}
  `;
  document.head.appendChild(style);
})();

